import { Injectable, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { BehaviorSubject, catchError, Observable, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';

import { App, SiteTemplate } from '../../models';
import { ERROR_CONSTANTS, SNACKBAR_CONSTANTS } from '../../shared/constants';
import { SiteTemplatesDataService } from '../data-services';

@Injectable({
    providedIn: 'root'
})
export class SiteTemplatesLogicService {
    private _completedEditOnForm: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    siteTemplates = signal<SiteTemplate[]>([]);

    completedEditOnForm$ = this._completedEditOnForm.asObservable();

    constructor(
        private _siteTemplatesDataService: SiteTemplatesDataService,
        private _snackbar: MatSnackBar,
    ) { }

    createSiteTemplate(currentApp: App, siteTemplate: SiteTemplate): Observable<SiteTemplate> {
        return this._siteTemplatesDataService.createSiteTemplate(currentApp, siteTemplate)
            .pipe(
                tap((template: SiteTemplate) => {
                    this.addCreatedSiteTemplateToLocalList(template);
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS(`${currentApp.aliases.site.singular} Template`, 'created'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }),
                catchError(() => {
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR(`${currentApp.aliases.site.singular} Template`, 'creating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                    return throwError(() => new Error(ERROR_CONSTANTS.ITEM_ACTION_ERROR(`${currentApp.aliases.site.singular} Template`, 'creating')));
                })
            );
    }

    getSiteTemplatesByProject(currentApp: App, projectId: string): Observable<SiteTemplate[]> {
        return this._siteTemplatesDataService.getSiteTemplatesByProject(currentApp.id, projectId)
            .pipe(
                tap((siteTemplates: SiteTemplate[]) => {
                    this.siteTemplates.set(siteTemplates || []);
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS(`${currentApp.aliases.site.singular} Templates`, 'loaded'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }),
                catchError(() => {
                    this.siteTemplates.set([]);
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR(`${currentApp.aliases.site.singular} Templates`, 'loading'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                    return throwError(() => new Error(ERROR_CONSTANTS.ITEM_ACTION_ERROR(`${currentApp.aliases.site.singular} Templates`, 'loading')));
                })
            );
    }

    getActiveSiteTemplatesByProject(currentApp: App, projectId: string): Observable<SiteTemplate[]> {
        return this._siteTemplatesDataService.getActiveSiteTemplatesByProject(currentApp.id, projectId)
            .pipe(
                tap((siteTemplates: SiteTemplate[]) => {
                    this.siteTemplates.set(siteTemplates || []);
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS(`${currentApp.aliases.site.singular} Templates`, 'loaded'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }),
                catchError(() => {
                    this.siteTemplates.set([]);
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR(`${currentApp.aliases.site.singular} Templates`, 'loading'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                    return throwError(() => new Error(ERROR_CONSTANTS.ITEM_ACTION_ERROR(`${currentApp.aliases.site.singular} Templates`, 'loading')));
                })
            );
    }

    updateSiteTemplate(currentApp: App, projectId: string, siteTemplate: SiteTemplate): Observable<SiteTemplate> {
        return this._siteTemplatesDataService.updateSiteTemplate(currentApp.id, projectId, siteTemplate)
            .pipe(
                tap((updatedTemplate: SiteTemplate) => {
                    if (!updatedTemplate) {
                        throw new Error(ERROR_CONSTANTS.ITEM_ACTION_ERROR(`${currentApp.aliases.site.singular} Template`, 'updating'));
                    }
                    this.updateLocalSiteTemplates(updatedTemplate);
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS(`${currentApp.aliases.site.singular} Template`, 'updated'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }),
                catchError((error) => {
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR(`${currentApp.aliases.site.singular} Template`, 'updating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                    return throwError(() => error);
                })
            );
    }

    updateSiteTemplateActiveStatus(currentApp: App, projectId: string, siteTemplate: SiteTemplate, newActiveStatus: boolean): Observable<SiteTemplate> {
        return this._siteTemplatesDataService.updateSiteTemplateActiveStatus(currentApp.id, projectId, siteTemplate, newActiveStatus)
            .pipe(
                tap((updatedTemplate: SiteTemplate) => {
                    if (!updatedTemplate) {
                        throw new Error(ERROR_CONSTANTS.ITEM_ACTION_ERROR(`${currentApp.aliases.site.singular} Template`, 'updating'));
                    }
                    this.updateLocalSiteTemplates(updatedTemplate);
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_TOGGLE_ACTIVE(`${currentApp.aliases.site.singular} Template`, siteTemplate.name, newActiveStatus), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }),
                catchError((error) => {
                    siteTemplate.active = !newActiveStatus;
                    this.updateLocalSiteTemplates(siteTemplate);
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR(`${currentApp.aliases.site.singular} Template`, 'updating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                    return throwError(() => error);
                })
            );
    }

    deleteSiteTemplate(currentApp: App, projectId: string, siteTemplateId: string): Observable<boolean> {
        return this._siteTemplatesDataService.deleteSiteTemplate(currentApp.id, projectId, siteTemplateId)
            .pipe(
                tap((success: boolean) => {
                    if (!success) {
                        throw new Error(ERROR_CONSTANTS.ITEM_ACTION_ERROR(`${currentApp.aliases.site.singular} Template`, 'deleting'));
                    }
                    this.siteTemplates.update(templates => templates.filter(t => t.id !== siteTemplateId));
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS(`${currentApp.aliases.site.singular} Template`, 'deleted'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }),
                catchError((error) => {
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR(`${currentApp.aliases.site.singular} Template`, 'deleting'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                    return throwError(() => error);
                })
            );
    }

    updateLocalSiteTemplates(updatedTemplate: SiteTemplate): void {
        this.siteTemplates.update(templates => {
            const index = templates.findIndex(t => t.id === updatedTemplate.id);
            if (index !== -1) {
                return [...templates.slice(0, index), updatedTemplate, ...templates.slice(index + 1)];
            }
            console.warn(ERROR_CONSTANTS.ITEM_ACTION_ERROR('Site Template', 'adding to local list'));
            return templates;
        });
    }

    addCreatedSiteTemplateToLocalList(createdTemplate: SiteTemplate): void {
        this.siteTemplates.update(templates => [...templates, createdTemplate]);
    }

    completedEdit(): void {
        this._completedEditOnForm.next(true);
    }
}
